import type { SimpleChanges } from '@angular/core';
import { Directive, Host, Injector, Input, Optional, SkipSelf } from '@angular/core';

import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ProtectedControlsContext } from './protected-controls.context';
import { ProtectedControlsService } from './protected-controls.service';

export function directiveContextFactory(
  directive: ProtectedControlsContextDirective,
  parent: ProtectedControlsContext,
): ProtectedControlsContext {
  return new ProtectedControlsContext(directive.crumb, parent || null);
}

@Directive({
  selector: '[rtProtectedControlsContext]',
  providers: [
    {
      provide: ProtectedControlsContext,
      useFactory: directiveContextFactory,
      deps: [
        [new Host(), ProtectedControlsContextDirective],
        [new Optional(), new SkipSelf(), ProtectedControlsContext],
      ],
    },
  ],
  standalone: true,
})
export class ProtectedControlsContextDirective {
  @Input('rtProtectedControlsContext') public crumb: string;

  @Input('rtProtectedControlsContextPayload')
  public set contextPayload(payload: any) {
    this.payloadBuffer.next(payload);
  }

  private protectedControlsContext: ProtectedControlsContext;
  private payloadBuffer = new BehaviorSubject<any>(null);

  constructor(
    private protectedControlsSrvc: ProtectedControlsService,
    private injector: Injector,
  ) {}

  public ngOnInit(): void {
    this.protectedControlsContext = this.injector.get(ProtectedControlsContext);
    this.updateContextCrumb(this.crumb);

    this.payloadBuffer
      .pipe(filter(value => value !== null && value !== undefined))
      .subscribe(payload => {
        this.protectedControlsSrvc.setContextPayload(this.protectedControlsContext, payload);
      });
  }

  public ngOnDestroy(): void {
    this.protectedControlsSrvc.clearContextPayload(this.protectedControlsContext);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { crumb } = changes;

    if (crumb && !crumb.firstChange && crumb.currentValue !== crumb.previousValue) {
      this.updateContextCrumb(crumb.currentValue);
    }
  }

  private updateContextCrumb(crumb: string): void {
    this.protectedControlsContext.crumb = crumb;
  }
}
